import { useEffect, useMemo, useState } from 'react';

import type { VideaNotesAppointment } from '@/modules/videaNotes/helpers';

import type { ClinicalNote } from '../../../react-query/videa-notes/useExistingNotes';
import type { TTreatmentResponse } from '../../claims/freeClaims/insuranceExport/types';

type SortField = 'patient' | 'appointment' | 'treatments';
type SortDirection = 'asc' | 'desc';

export const useAmbientSorting = (
  appointmentsWithTreatmentsAndNotes: (VideaNotesAppointment & {
    treatments: TTreatmentResponse[];
    notes: ClinicalNote[];
  })[],
  isRecentNotesView: boolean = false,
) => {
  // Sorting state - default to desc when in recent notes view
  const [sortField, setSortField] = useState<SortField>('appointment');
  const [sortDirection, setSortDirection] = useState<SortDirection>(
    isRecentNotesView ? 'desc' : 'asc',
  );

  // Update sort direction when switching between normal and recent notes view
  useEffect(() => {
    // Only auto-adjust when sorting by appointment field
    if (sortField === 'appointment') {
      setSortDirection(isRecentNotesView ? 'desc' : 'asc');
    }
  }, [isRecentNotesView, sortField]);

  // Sort appointments with treatments and notes
  const sortedAppointments = useMemo(() => {
    const sorted = [...appointmentsWithTreatmentsAndNotes].sort((a, b) => {
      const direction = sortDirection === 'asc' ? 1 : -1;

      switch (sortField) {
        case 'patient': {
          const nameA =
            `${a.patient.firstName} ${a.patient.lastName}`.toLowerCase();
          const nameB =
            `${b.patient.firstName} ${b.patient.lastName}`.toLowerCase();
          return nameA.localeCompare(nameB) * direction;
        }
        case 'appointment': {
          // Use noteCreatedAt for recent notes view, otherwise use appointment start time
          const dateA = isRecentNotesView && a.appointment.noteCreatedAt
            ? new Date(a.appointment.noteCreatedAt).getTime()
            : new Date(a.appointment.start).getTime();
          const dateB = isRecentNotesView && b.appointment.noteCreatedAt
            ? new Date(b.appointment.noteCreatedAt).getTime()
            : new Date(b.appointment.start).getTime();
          return (dateA - dateB) * direction;
        }
        case 'treatments': {
          // Sort by number of treatments, then by appointment date
          const treatmentCountA = a.treatments?.length || 0;
          const treatmentCountB = b.treatments?.length || 0;

          if (treatmentCountA !== treatmentCountB) {
            return (treatmentCountA - treatmentCountB) * direction;
          }

          // If same number of treatments, sort by appointment date
          const dateA = new Date(a.appointment.start).getTime();
          const dateB = new Date(b.appointment.start).getTime();
          return (dateA - dateB) * direction;
        }
        default:
          return 0;
      }
    });

    return sorted;
  }, [appointmentsWithTreatmentsAndNotes, sortField, sortDirection, isRecentNotesView]);

  const handleSort = (field: string, direction: SortDirection) => {
    setSortField(field as SortField);
    setSortDirection(direction);
  };

  return {
    sortedAppointments,
    sortField,
    sortDirection,
    handleSort,
  };
};
